import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Trash, EnvelopeSimple, Crown } from "@phosphor-icons/react";

export default function UsersAdmin() {
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role_id: "", contractor_id: "", customer_handle: "" });
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    const [m, r, c, cu] = await Promise.all([
      api.get("/users"), api.get("/roles"), api.get("/contractors"), api.get("/customers"),
    ]);
    setMembers(m.data);
    setRoles(r.data);
    setContractors(c.data);
    setCustomers(cu.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rolesById = useMemo(() => {
    const map = {};
    for (const r of roles) map[r.id] = r;
    return map;
  }, [roles]);

  const isContractorRole = (roleId) => !!rolesById[roleId]?.is_contractor_role;
  const isCustomerRole = (roleId) => !!rolesById[roleId]?.is_customer_role;

  const invite = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.role_id) return toast.error("Preencha e-mail e perfil");
    if (isContractorRole(form.role_id) && !form.contractor_id) {
      return toast.error("Perfil Contratante exige um contratante vinculado");
    }
    if (isCustomerRole(form.role_id) && !form.customer_handle) {
      return toast.error("Perfil Cliente exige uma @arroba vinculada");
    }
    setInviting(true);
    try {
      await api.post("/users/invite", {
        email: form.email,
        name: form.name || null,
        role_id: form.role_id,
        contractor_id: form.contractor_id || null,
        customer_handle: form.customer_handle || null,
      });
      toast.success("Convite criado!");
      setForm({ email: "", name: "", role_id: "", contractor_id: "", customer_handle: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao convidar");
    }
    setInviting(false);
  };

  const changeRole = async (m, role_id) => {
    try {
      const needsContractor = isContractorRole(role_id);
      const needsCustomer = isCustomerRole(role_id);
      const contractor_id = needsContractor ? (m.contractor_id || null) : null;
      const customer_handle = needsCustomer ? (m.customer_handle || null) : null;
      if (needsContractor && !contractor_id) {
        return toast.error("Antes de aplicar o perfil Contratante, selecione o contratante vinculado.");
      }
      if (needsCustomer && !customer_handle) {
        return toast.error("Antes de aplicar o perfil Cliente, selecione a @arroba vinculada.");
      }
      await api.put(`/users/${m.id}`, { role_id, contractor_id, customer_handle });
      toast.success("Perfil atualizado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const changeContractor = async (m, contractor_id) => {
    try {
      await api.put(`/users/${m.id}`, {
        role_id: m.role_id,
        contractor_id: contractor_id || null,
        customer_handle: m.customer_handle || null,
      });
      toast.success("Vínculo atualizado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const changeCustomer = async (m, customer_handle) => {
    try {
      await api.put(`/users/${m.id}`, {
        role_id: m.role_id,
        contractor_id: m.contractor_id || null,
        customer_handle: customer_handle || null,
      });
      toast.success("Vínculo atualizado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const revoke = async (m) => {
    if (!window.confirm(`Remover ${m.email} da loja?`)) return;
    try {
      await api.delete(`/users/${m.id}`);
      toast.success("Usuário removido");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const nonMasterRoles = roles.filter(r => !r.is_master_role);

  return (
    <div data-testid="users-admin-page" className="space-y-8">
      <header>
        <p className="label-brutal">Administração</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black">Usuários</h1>
        <p className="text-brand-ink2 mt-2">Convide sua equipe. Usuários com perfil <strong>Contratante</strong> só veem as próprias lives e o relatório de prestação de contas.</p>
      </header>

      <form onSubmit={invite} className="card-brutal p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.2fr_1fr_1fr_1fr_1fr_auto] gap-3 items-end">
        <div>
          <label className="label-brutal">E-mail Google *</label>
          <input data-testid="invite-email" required type="email" className="input-brutal" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ana@example.com" />
        </div>
        <div>
          <label className="label-brutal">Nome (opcional)</label>
          <input data-testid="invite-name" className="input-brutal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ana Vendedora" />
        </div>
        <div>
          <label className="label-brutal">Perfil *</label>
          <select data-testid="invite-role" required className="input-brutal" value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value, contractor_id: "", customer_handle: "" })}>
            <option value="">— escolha —</option>
            {nonMasterRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-brutal">Contratante {isContractorRole(form.role_id) && "*"}</label>
          <select
            data-testid="invite-contractor"
            className="input-brutal"
            value={form.contractor_id}
            onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}
            disabled={!isContractorRole(form.role_id)}
          >
            <option value="">— nenhum —</option>
            {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-brutal">Cliente (@arroba) {isCustomerRole(form.role_id) && "*"}</label>
          <select
            data-testid="invite-customer"
            className="input-brutal"
            value={form.customer_handle}
            onChange={(e) => setForm({ ...form, customer_handle: e.target.value })}
            disabled={!isCustomerRole(form.role_id)}
          >
            <option value="">— nenhum —</option>
            {customers.map(c => <option key={c.id} value={c.handle}>{c.handle} — {c.full_name}</option>)}
          </select>
        </div>
        <button data-testid="invite-submit" type="submit" disabled={inviting} className="btn-pink h-[52px]">
          <EnvelopeSimple weight="bold" size={16} /> Convidar
        </button>
      </form>

      <div className="card-brutal overflow-x-auto">
        <table className="tbl w-full">
          <thead>
            <tr>
              <th>E-mail</th>
              <th>Nome</th>
              <th>Perfil</th>
              <th>Contratante</th>
              <th>Cliente (@arroba)</th>
              <th>Status</th>
              <th className="w-24 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan={7} className="text-center text-brand-ink2 py-8">Nenhum usuário.</td></tr>
            )}
            {members.map((m) => {
              const contractorRole = !!m.is_contractor_role;
              const customerRole = !!m.is_customer_role;
              return (
                <tr key={m.id} data-testid={`user-row-${m.id}`}>
                  <td className="font-bold">{m.email}</td>
                  <td>{m.name || "—"}</td>
                  <td>
                    {m.is_master ? (
                      <span className="badge-brutal !bg-brand-pink !text-white"><Crown weight="bold" size={12} /> Master</span>
                    ) : (
                      <select
                        data-testid={`role-select-${m.id}`}
                        className="input-brutal !py-1 !px-2 !text-sm"
                        value={m.role_id}
                        onChange={(e) => changeRole(m, e.target.value)}
                      >
                        {nonMasterRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td>
                    {m.is_master ? (
                      <span className="text-brand-ink2 text-sm">—</span>
                    ) : (
                      <select
                        data-testid={`contractor-select-${m.id}`}
                        className="input-brutal !py-1 !px-2 !text-sm"
                        value={m.contractor_id || ""}
                        onChange={(e) => changeContractor(m, e.target.value)}
                        disabled={!contractorRole && !m.contractor_id}
                      >
                        <option value="">— nenhum —</option>
                        {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td>
                    {m.is_master ? (
                      <span className="text-brand-ink2 text-sm">—</span>
                    ) : (
                      <select
                        data-testid={`customer-select-${m.id}`}
                        className="input-brutal !py-1 !px-2 !text-sm"
                        value={m.customer_handle || ""}
                        onChange={(e) => changeCustomer(m, e.target.value)}
                        disabled={!customerRole && !m.customer_handle}
                      >
                        <option value="">— nenhum —</option>
                        {customers.map(c => <option key={c.id} value={c.handle}>{c.handle}</option>)}
                      </select>
                    )}
                  </td>
                  <td>
                    {m.status === "active"
                      ? <span className="badge-brutal !bg-brand-success !text-white">Ativo</span>
                      : <span className="badge-brutal !bg-brand-yellow">Pendente</span>}
                  </td>
                  <td className="text-right">
                    {!m.is_master && (
                      <button data-testid={`revoke-${m.id}`} onClick={() => revoke(m)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-peach">
                        <Trash weight="bold" size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
